// Invoice/manual entry retain source currency and bank files use preview-first flows.
// One-off savings dates default to the device's calendar day, matching the ledger's local-day filter.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Camera, Images, Landmark, PenLine, PiggyBank } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { compressImage } from '@/lib/image';
import { runQueue } from '@/lib/uploadQueue';
import { useWorkspaceStore } from '@/stores/workspace';
import { PageLayout, PageLink } from '@/components/PageLayout';
import { toast } from '@/lib/toast';
import { BankImportPanel } from '@/components/reconciliation/BankImportPanel';
import { QuickReceiptForm } from '@/components/QuickReceiptForm';
import { CategoryHint } from '@/components/CategoryHint';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';
const MAX_IMAGES = 10;
const BATCH_CONCURRENCY = 2;

interface PendingImage {
  id: string;
  kind: 'image';
  blob: Blob;
  dataUrl: string;
}

interface PendingPdf {
  id: string;
  kind: 'pdf';
  blob: Blob;
  filename: string;
  size: number;
}

type PendingFile = PendingImage | PendingPdf;

interface ListImportInserted {
  id: string;
  merchant: string | null;
  merchant_zh: string | null;
  receipt_date: string | null;
  total_amount: number;
  currency: string;
}

interface ListImportSkipped {
  merchant: string | null;
  merchant_zh: string | null;
  receipt_date: string | null;
  total_amount: number;
  currency: string;
  existing_id: string;
  existing_merchant: string | null;
}

interface ListImportResult {
  mode: 'list';
  inserted: ListImportInserted[];
  skipped: ListImportSkipped[];
}

interface SingleImportResult {
  mode: 'single';
  receipt: { id: string };
}

interface DuplicateUploadResult {
  mode: 'duplicate';
  duplicate_of: string;
  merchant: string | null;
}

type UploadResponse = ListImportResult | SingleImportResult | DuplicateUploadResult;

export type UploadMode = 'single' | 'batch' | 'cash' | 'savings' | 'bank';

function recognizedMode(value: string | null): UploadMode {
  return value === 'batch' || value === 'cash' || value === 'savings' || value === 'bank' ? value : 'single';
}

interface CashCategoryRow {
  id: string;
  name: string;
  name_zh: string | null;
}

interface ParsedCashItem {
  name: string;
  quantity: number;
  unit_price: number | null;
  subtotal: number | null;
}

interface ParsedCash {
  merchant: string | null;
  receipt_date: string;
  receipt_time: string | null;
  total_amount: number | null;
  currency: string;
  items: ParsedCashItem[];
  category_id: string | null;
  confidence: number;
  ai_model: string;
  raw_text: string;
}

interface ManualSavePayload {
  merchant: string | null;
  receipt_date: string;
  receipt_time: string | null;
  currency: string;
  total_amount: number;
  category_id: string | null;
  notes: string | null;
  ai_model: string | null;
  ai_confidence: number | null;
  items: Array<{
    name: string;
    quantity: number;
    unit_price: number | null;
    subtotal: number;
  }>;
}

type BatchItemStatus = 'queued' | 'uploading' | 'done' | 'failed';

interface BatchItem {
  id: string;
  blob: Blob;
  dataUrl: string;
  filename: string;
  status: BatchItemStatus;
  startedAt?: number;
  receiptId?: string;
  error?: string;
}

const PDF_MIME = 'application/pdf';
const MAX_PDF_BYTES = 10 * 1024 * 1024;

// Samsung Internet's intent chooser is broken for our split-MIME approach:
// a `<input accept="application/pdf">` opens a chooser of 2 cameras + the
// gallery app, none of which can pick PDFs (Samsung's «My Files» doesn't
// register `application/pdf` in its manifest, so it gets dropped). For
// Samsung we fall back to one unified «Choose file» button with mixed
// MIMEs — that chooser DOES include «My Files» / «Photos and videos», and
// the user can navigate to a PDF via «Photos and videos».
// Chrome and Safari don't need this — split picker works cleanly.
const MIXED_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

function isSamsungInternet(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /SamsungBrowser/i.test(navigator.userAgent);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function ListImportSummary(props: {
  result: ListImportResult;
  onUploadAnother: () => void;
  onViewAll: () => void;
}) {
  const { result, onUploadAnother, onViewAll } = props;
  const { inserted, skipped } = result;
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-card p-5">
        <div className="text-base font-semibold">
          {inserted.length === 0
            ? t('capture.importedNone')
            : t('capture.importedHeading', { count: inserted.length })}
        </div>
        {skipped.length > 0 && (
          <div className="mt-1 text-xs text-muted-foreground">
            {t('capture.skippedNote', { count: skipped.length })}
          </div>
        )}
      </div>

      {inserted.length > 0 && (
        <div className="rounded-2xl border bg-card">
          <div className="border-b px-4 py-2 text-xs font-medium text-muted-foreground">
            {t('capture.importedSection')}
          </div>
          <ul className="divide-y">
            {inserted.map((it) => (
              <li key={it.id}>
                <Link
                  to={`/receipts/${it.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted"
                >
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="line-clamp-2 text-sm font-medium">
                      {it.merchant ?? t('capture.unknownMerchant')}
                    </div>
                    {it.merchant_zh && (
                      <div className="line-clamp-2 text-xs text-muted-foreground">
                        {it.merchant_zh}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {it.receipt_date ?? '—'}
                    </div>
                  </div>
                  <div className="text-sm tabular-nums">
                    {formatMoney(it.total_amount, it.currency)}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="rounded-2xl border bg-card">
          <div className="border-b px-4 py-2 text-xs font-medium text-muted-foreground">
            {t('capture.alreadyInRecords')}
          </div>
          <ul className="divide-y">
            {skipped.map((s, i) => (
              <li key={`${s.existing_id}-${i}`}>
                <Link
                  to={`/receipts/${s.existing_id}`}
                  className="flex items-center justify-between px-4 py-3 text-muted-foreground hover:bg-muted"
                >
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="line-clamp-2 text-sm">
                      {s.merchant ?? s.existing_merchant ?? t('capture.unknown')}
                    </div>
                    {s.merchant_zh && (
                      <div className="line-clamp-2 text-xs">{s.merchant_zh}</div>
                    )}
                    <div className="text-xs">{s.receipt_date ?? '—'}</div>
                  </div>
                  <div className="text-sm tabular-nums">
                    {formatMoney(s.total_amount, s.currency)}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={onUploadAnother}
          className="flex-1 rounded-md border py-2 text-sm hover:bg-muted"
        >
          {t('capture.uploadAnother')}
        </button>
        <button
          onClick={onViewAll}
          className="flex-1 rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          {t('capture.viewAllReceipts')}
        </button>
      </div>
    </div>
  );
}

// Each route owns one capture method; remounting resets transient file and preview state.
export function CapturePage({ mode: routeMode }: { mode?: UploadMode } = {}) {
  const [searchParams] = useSearchParams();
  const mode = routeMode ?? recognizedMode(searchParams.get('mode'));
  return <CaptureMethodPage key={mode} mode={mode} />;
}

export function CaptureChooserPage() {
  const { t, i18n } = useTranslation();
  const zh = i18n.language.startsWith('zh');
  return <PageLayout title={zh ? '记一笔' : 'Add a record'} subtitle={zh ? '选择这次的录入方式' : 'Choose how to add this record'} backTo="/ledger">
    <section className="space-y-2" aria-label={zh ? '消费录入' : 'Invoice entry'}>
      <PageLink to="/capture/receipt" icon={Camera} title={zh ? '拍照 / 上传票据' : 'Photo / upload receipt'} description={t('capture.modeSingleDesc')} />
      <PageLink to="/capture/batch" icon={Images} title={t('capture.modeBatchLabel')} description={t('capture.modeBatchDesc')} />
      <PageLink to="/capture/manual" icon={PenLine} title={zh ? '手动录入' : 'Manual entry'} description={zh ? '直接填写，或用一句话生成待确认账单' : 'Fill in a record or describe it for review'} />
    </section>
    <section className="space-y-2" aria-label={zh ? '其他记录' : 'Other records'}>
      <h2 className="px-1 text-xs font-medium text-muted-foreground">{zh ? '其他记录' : 'Other records'}</h2>
      <PageLink to="/ledger/reconcile" icon={Landmark} title={zh ? '银行账单核对' : 'Bank reconciliation'} description={zh ? '导入银行流水，与已有账单核对' : 'Import bank transactions and match existing invoices'} />
      <PageLink to="/capture/savings" icon={PiggyBank} title={zh ? '记录一次储蓄' : 'Record a savings deposit'} description={zh ? '填写本次存入的金额和日期' : 'Enter the amount and date for this deposit'} />
      <PageLink to="/finance/savings" icon={PiggyBank} title={zh ? '管理储蓄计划' : 'Manage savings plans'} description={zh ? '查看周期计划、待办与存入进展' : 'View recurring plans, scheduled deposits and progress'} />
    </section>
  </PageLayout>;
}

function CaptureMethodPage({ mode }: { mode: UploadMode }) {
  const navigate = useNavigate();
  const ws = useWorkspaceStore((s) => s.current);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const samsungFileInputRef = useRef<HTMLInputElement>(null);

  // Snapshot once on mount so SSR/hydration can't flip the layout.
  const [isSamsung] = useState<boolean>(() => isSamsungInternet());

  const [items, setItems] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [listResult, setListResult] = useState<ListImportResult | null>(null);

  const { i18n } = useTranslation();
  const zh = i18n.language.startsWith('zh');
  const [captureCurrency, setCaptureCurrency] = useState(() => localStorage.getItem(`spent-tracker:capture-currency:${ws?.id}`) || ws?.default_currency || 'AUD');
  const [batchItems, setBatchItems] = useState<BatchItem[]>([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchTick, setBatchTick] = useState(0);
  const batchAbortRef = useRef<AbortController | null>(null);

  const cashCategoriesQuery = useQuery<CashCategoryRow[]>({
    queryKey: ['categories', ws?.id],
    queryFn: () => api.get<CashCategoryRow[]>('/categories'),
    enabled: !!ws,
  });

  const [cashText, setCashText] = useState('');
  const [cashError, setCashError] = useState<string | null>(null);
  const [cashPreview, setCashPreview] = useState<ParsedCash | null>(null);

  const parseTextMutation = useMutation({
    mutationFn: (text: string) =>
      api.post<ParsedCash>('/receipts/parse-text', {
        text,
        default_currency: captureCurrency,
        categories: (cashCategoriesQuery.data ?? []).map((c) => ({
          id: c.id,
          name: c.name,
          name_zh: c.name_zh,
        })),
      }),
    onSuccess: (data) => {
      setCashError(null);
      setCashPreview(data);
    },
    onError: (err) => {
      setCashError(err instanceof Error ? err.message : 'parse_failed');
    },
  });

  const saveManualMutation = useMutation({
    mutationFn: (payload: ManualSavePayload) =>
      api.post<{ id: string }>('/receipts/manual', payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['receipts-v2', ws?.id] });
      navigate(`/receipts/${data.id}`);
    },
    onError: (err) => {
      setCashError(err instanceof Error ? err.message : 'save_failed');
    },
  });

  // Tick a 1-second counter while uploading OR parsing cash text so the user
  // sees real progress on every long Gemini call, not just image upload.
  const isParsingCash = parseTextMutation.isPending;
  useEffect(() => {
    if (!uploading && !isParsingCash) {
      setElapsedSec(0);
      return;
    }
    const start = Date.now();
    const id = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - start) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [uploading, isParsingCash]);

  // Per-item elapsed counter while batch is running (single ticker shared).
  useEffect(() => {
    if (!batchRunning) return;
    const id = window.setInterval(() => setBatchTick((n) => n + 1), 500);
    return () => window.clearInterval(id);
  }, [batchRunning]);

  // Warn before unload if batch has work in flight.
  useEffect(() => {
    if (!batchRunning) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [batchRunning]);

  const stagedKind: 'image' | 'pdf' | null = items[0]?.kind ?? null;
  const hasPdf = stagedKind === 'pdf';
  const imageCount = items.filter((it) => it.kind === 'image').length;

  function isPdfFile(file: File): boolean {
    // Some browsers report empty type for files dragged from Files app.
    // Filename suffix is the cheap second check.
    return file.type === PDF_MIME || file.name.toLowerCase().endsWith('.pdf');
  }

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setError(null);

    try {
      // Batch mode: only images, each becomes its own queue item.
      if (mode === 'batch') {
        if (files.some(isPdfFile)) {
          setError(t('capture.batchPdfNotInBatch'));
          return;
        }
        const remaining = MAX_IMAGES - batchItems.length;
        if (remaining <= 0) {
          setError(t('capture.tooManyImages', { max: MAX_IMAGES }));
          return;
        }
        const accepted = files.slice(0, remaining);
        const compressed = await Promise.all(
          accepted.map(async (file): Promise<BatchItem> => {
            const result = await compressImage(file);
            return {
              id: crypto.randomUUID(),
              blob: result.blob,
              dataUrl: result.dataUrl,
              filename: file.name || 'image.jpg',
              status: 'queued',
            };
          }),
        );
        setBatchItems((prev) => [...prev, ...compressed]);
        return;
      }

      const incomingHasPdf = files.some(isPdfFile);
      const incomingHasImage = files.some((f) => !isPdfFile(f));

      // Don't allow mixing within a single picker selection.
      if (incomingHasPdf && incomingHasImage) {
        setError(t('capture.mixedError'));
        return;
      }

      // Don't allow mixing with what's already staged.
      if (incomingHasPdf && stagedKind === 'image') {
        setError(t('capture.alreadyHasImages'));
        return;
      }
      if (incomingHasImage && stagedKind === 'pdf') {
        setError(t('capture.alreadyHasPdf'));
        return;
      }

      if (incomingHasPdf) {
        if (files.length !== 1 || stagedKind === 'pdf') {
          setError(t('capture.pdfMultiple'));
          return;
        }
        const pdf = files[0];
        if (pdf.size > MAX_PDF_BYTES) {
          setError(t('capture.pdfTooLarge', { maxMB: MAX_PDF_BYTES / 1024 / 1024 }));
          return;
        }
        setItems([
          {
            id: crypto.randomUUID(),
            kind: 'pdf',
            blob: pdf,
            filename: pdf.name,
            size: pdf.size,
          },
        ]);
        return;
      }

      // Image branch
      const remaining = MAX_IMAGES - imageCount;
      if (remaining <= 0) {
        setError(t('capture.tooManyImages', { max: MAX_IMAGES }));
        return;
      }
      const accepted = files.slice(0, remaining);
      const compressed = await Promise.all(
        accepted.map(async (file): Promise<PendingImage> => {
          const result = await compressImage(file);
          return {
            id: crypto.randomUUID(),
            kind: 'image',
            blob: result.blob,
            dataUrl: result.dataUrl,
          };
        }),
      );
      setItems((prev) => [...prev, ...compressed]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('capture.failedToReadFile'));
    } finally {
      // Allow re-picking the same file
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
      if (pdfInputRef.current) pdfInputRef.current.value = '';
      if (samsungFileInputRef.current) samsungFileInputRef.current.value = '';
    }
  }

  function removeItem(id: string) {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }

  function moveItem(id: string, dir: -1 | 1) {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.id === id);
      if (idx === -1) return prev;
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }

  async function handleSubmit() {
    if (items.length === 0 || !ws) return;
    setUploading(true);
    setError(null);
    setProgress(
      hasPdf
        ? t('capture.progressPdf')
        : items.length === 1
          ? t('capture.progressSingle')
          : t('capture.progressMulti', { count: items.length }),
    );

    try {
      const formData = new FormData();
      formData.append('default_currency', captureCurrency);
      for (const [i, it] of items.entries()) {
        if (it.kind === 'pdf') {
          formData.append('image', it.blob, it.filename || `receipt-${i}.pdf`);
        } else {
          formData.append('image', it.blob, `image-${i}.jpg`);
        }
      }

      const res = await fetch(`${API_BASE}/receipts/upload`, {
        method: 'POST',
        // Browser sets multipart Content-Type with boundary automatically.
        headers: { 'X-Workspace-Id': ws.id },
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          (body && typeof body === 'object' && 'error' in body && (body as { error: string }).error) ||
            `${t('capture.uploadFailed')}: ${res.status}`,
        );
      }

      const data = (await res.json()) as UploadResponse;
      await queryClient.invalidateQueries({ queryKey: ['receipts-v2', ws.id] });

      if (data.mode === 'list') {
        // Stay on this page and show the import summary; user decides what
        // to do next (open one, view all, upload another).
        setUploading(false);
        setProgress('');
        setItems([]);
        setListResult(data);
      } else if (data.mode === 'duplicate') {
        // Same image bytes were uploaded before. Skip AI, jump to original
        // receipt with a one-shot banner flag.
        navigate(`/receipts/${data.duplicate_of}`, {
          replace: true,
          state: { dupUpload: true, merchant: data.merchant },
        });
      } else {
        navigate(`/receipts/${data.receipt.id}`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('capture.uploadFailed'));
      setUploading(false);
      setProgress('');
    }
  }

  function handleReset() {
    setItems([]);
    setError(null);
    setProgress('');
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    if (pdfInputRef.current) pdfInputRef.current.value = '';
    if (samsungFileInputRef.current) samsungFileInputRef.current.value = '';
  }

  function patchBatchItem(id: string, patch: Partial<BatchItem>) {
    setBatchItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    );
  }

  function removeBatchItem(id: string) {
    setBatchItems((prev) => prev.filter((it) => it.id !== id));
  }

  function handleBatchReset() {
    if (batchAbortRef.current) {
      batchAbortRef.current.abort();
      batchAbortRef.current = null;
    }
    setBatchItems([]);
    setError(null);
    setBatchRunning(false);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  }

  function handleBatchCancel() {
    if (batchAbortRef.current) batchAbortRef.current.abort();
  }

  async function uploadOneBatchImage(
    item: { id: string; blob: Blob; filename: string },
    signal: AbortSignal,
  ): Promise<SingleImportResult> {
    if (!ws) throw new Error(t('capture.uploadFailed'));
    const fd = new FormData();
    fd.append('default_currency', captureCurrency);
    fd.append('image', item.blob, item.filename || `${item.id}.jpg`);
    const res = await fetch(`${API_BASE}/receipts/upload`, {
      method: 'POST',
      headers: { 'X-Workspace-Id': ws.id },
      credentials: 'include',
      body: fd,
      signal,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: string }
        | null;
      const msg =
        (body && body.error) || `${t('capture.uploadFailed')}: ${res.status}`;
      const err = new Error(msg) as Error & { status?: number };
      err.status = res.status;
      throw err;
    }
    const data = (await res.json()) as UploadResponse;
    if (data.mode === 'list') {
      // Edge case: user selected a Samsung/Google Pay screenshot in batch mode.
      // Treat as a single batch slot that succeeded — the inserted rows are
      // already in the DB; we just mark this slot "done" without a single id.
      return { mode: 'single', receipt: { id: '' } };
    }
    if (data.mode === 'duplicate') {
      // Same image already on file. Mark slot done pointing at the original.
      return { mode: 'single', receipt: { id: data.duplicate_of } };
    }
    return data;
  }

  async function runBatch(idsToRun: string[]) {
    if (!ws || idsToRun.length === 0) return;
    setError(null);
    setBatchRunning(true);
    const ac = new AbortController();
    batchAbortRef.current = ac;

    // Snapshot the items we plan to send (avoid stale closures on retries).
    const snapshot = batchItems.filter((it) => idsToRun.includes(it.id));
    const payloads = snapshot.map((it) => ({
      id: it.id,
      payload: { id: it.id, blob: it.blob, filename: it.filename },
    }));

    try {
      await runQueue(payloads, {
        concurrency: BATCH_CONCURRENCY,
        worker: uploadOneBatchImage,
        maxRetries: 1,
        retryDelayMs: 1000,
        shouldRetry: (err) => {
          const status = (err as { status?: number })?.status;
          if (status == null) return true; // network / abort errors retry once
          return status === 429 || status >= 500;
        },
        signal: ac.signal,
        onItemStart: (id) => {
          patchBatchItem(id, {
            status: 'uploading',
            startedAt: Date.now(),
            error: undefined,
          });
        },
        onItemEnd: (id, update) => {
          if (update.status === 'done') {
            patchBatchItem(id, {
              status: 'done',
              receiptId: update.result?.receipt.id,
              error: undefined,
            });
          } else {
            patchBatchItem(id, {
              status: 'failed',
              error: update.error || t('capture.uploadFailed'),
            });
          }
        },
      });
    } finally {
      setBatchRunning(false);
      batchAbortRef.current = null;
      await queryClient.invalidateQueries({ queryKey: ['receipts-v2', ws.id] });
    }
  }

  function handleBatchSubmit() {
    const queuedIds = batchItems
      .filter((it) => it.status === 'queued')
      .map((it) => it.id);
    if (queuedIds.length === 0) return;
    void runBatch(queuedIds);
  }

  function handleBatchRetryFailed() {
    const failed = batchItems.filter((it) => it.status === 'failed');
    if (failed.length === 0) return;
    const ids = failed.map((it) => it.id);
    setBatchItems((prev) =>
      prev.map((it) =>
        ids.includes(it.id) ? { ...it, status: 'queued', error: undefined } : it,
      ),
    );
    void runBatch(ids);
  }

  const batchDoneCount = batchItems.filter((it) => it.status === 'done').length;
  const batchFailedCount = batchItems.filter((it) => it.status === 'failed').length;
  const batchQueuedCount = batchItems.filter((it) => it.status === 'queued').length;
  const batchUploadingCount = batchItems.filter((it) => it.status === 'uploading').length;
  const batchHasFailed = batchFailedCount > 0;
  const batchHasQueued = batchQueuedCount > 0;
  const batchAllSettled =
    batchItems.length > 0 && !batchRunning && batchQueuedCount === 0;
  const batchCanAddMore =
    !batchRunning && batchItems.length < MAX_IMAGES;

  const canAddMore = !hasPdf && imageCount < MAX_IMAGES;

  return (
    <PageLayout
      title={mode === 'single' ? (zh ? '拍照 / 上传票据' : 'Photo / upload receipt') : mode === 'batch' ? t('capture.modeBatchLabel') : mode === 'cash' ? (zh ? '手动录入' : 'Manual entry') : mode === 'savings' ? t('capture.chipSavings') : (zh ? '导入银行账单' : 'Import bank statement')}
      subtitle={mode === 'single' ? t('capture.modeSingleDesc') : mode === 'batch' ? t('capture.modeBatchDesc') : mode === 'cash' ? t('capture.cashEntry.modeDesc') : mode === 'savings' ? t('capture.savingsFormHint') : undefined}
      backTo={mode === 'bank' ? '/ledger/reconcile' : '/capture'}
    >
      <div className="space-y-4">
        {mode !== 'bank' && mode !== 'savings' && !listResult && <div className="space-y-3">
          <label className="block rounded-xl border bg-card p-3 text-sm">{zh ? '本次票据默认币种' : 'Default invoice currency'}<select disabled={uploading || batchRunning} value={captureCurrency} onChange={(e) => { setCaptureCurrency(e.target.value); localStorage.setItem(`spent-tracker:capture-currency:${ws?.id}`, e.target.value); }} className="mt-2 min-h-[44px] w-full rounded-lg border bg-background px-3">{Array.from(new Set([ws?.default_currency || 'AUD', 'NZD', 'AUD', 'USD', 'EUR', 'GBP', 'CNY', 'JPY'])).map((code) => <option key={code}>{code}</option>)}</select><span className="mt-2 block text-xs text-muted-foreground">{zh ? '旅行可选 NZD；家庭统计保持 AUD。票据有明确币种时以票据为准。' : 'Choose NZD while travelling. Family reporting stays AUD; explicit invoice currency takes priority.'}</span></label>
          {mode === 'cash' && <QuickReceiptForm defaultCurrency={captureCurrency} />}
        </div>}
        {mode === 'bank' && <BankImportPanel />}

        {listResult && (
          <ListImportSummary
            result={listResult}
            onUploadAnother={() => {
              setListResult(null);
              handleReset();
            }}
            onViewAll={() => navigate('/ledger')}
          />
        )}

        {/*
          Three separate inputs, each with a single MIME class. On Android
          (especially Samsung), a mixed accept list (image + PDF in one input)
          gets dispatched to a chooser of multiple apps where some honour the
          full MIME list and some don't (e.g. Samsung «My Files» silently
          filters to images). One MIME class per input → picker routing is
          unambiguous on every platform.
        */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={handleFiles}
          className="hidden"
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFiles}
          className="hidden"
        />
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleFiles}
          className="hidden"
        />
        {/* Samsung-only fallback input. See MIXED_ACCEPT comment above. */}
        <input
          ref={samsungFileInputRef}
          type="file"
          accept={MIXED_ACCEPT}
          multiple
          onChange={handleFiles}
          className="hidden"
        />

        {!listResult && mode === 'single' && (items.length === 0 ? (
          <div className="rounded-2xl border bg-card p-8 text-center">
            <p className="mb-4 text-sm text-muted-foreground">
              {t('capture.hint').split('\n').map((line, i) => (
                <span key={i}>{i > 0 && <br />}{line}</span>
              ))}
            </p>
            {isSamsung ? (
              <>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
                  >
                    📷 {t('capture.takePhoto')}
                  </button>
                  <button
                    onClick={() => samsungFileInputRef.current?.click()}
                    className="rounded-md border px-6 py-3 text-sm font-medium hover:bg-muted"
                  >
                    📁 {t('capture.chooseFile')}
                  </button>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  {t('capture.samsungHint', {
                    bold: (chunks: string) => <strong>{chunks}</strong>,
                  })}
                </p>
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  📷 {t('capture.takePhoto')}
                </button>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  className="rounded-md border px-6 py-3 text-sm font-medium hover:bg-muted"
                >
                  🖼️ {t('capture.chooseImage')}
                </button>
                <button
                  onClick={() => pdfInputRef.current?.click()}
                  className="rounded-md border px-6 py-3 text-sm font-medium hover:bg-muted"
                >
                  📄 {t('capture.choosePdf')}
                </button>
              </div>
            )}
            {error && (
              <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-3">
              {items.map((it, i) => (
                <div
                  key={it.id}
                  className="overflow-hidden rounded-2xl border bg-card"
                >
                  <div className="flex items-center justify-between px-3 py-2 text-xs text-muted-foreground">
                    <span>
                      {it.kind === 'pdf'
                        ? t('capture.pdfReceipt')
                        : t('capture.sectionOf', { n: i + 1, total: items.length })}
                    </span>
                    {!uploading && (
                      <div className="flex items-center gap-1">
                        {it.kind === 'image' && items.length > 1 && (
                          <>
                            <button
                              onClick={() => moveItem(it.id, -1)}
                              disabled={i === 0}
                              className="rounded px-1.5 py-0.5 hover:bg-muted disabled:opacity-30"
                              aria-label={t('capture.moveUp')}
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => moveItem(it.id, 1)}
                              disabled={i === items.length - 1}
                              className="rounded px-1.5 py-0.5 hover:bg-muted disabled:opacity-30"
                              aria-label={t('capture.moveDown')}
                            >
                              ↓
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => removeItem(it.id)}
                          className="rounded px-1.5 py-0.5 text-destructive hover:bg-destructive/10"
                          aria-label={t('capture.remove')}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                  {it.kind === 'image' ? (
                    <img
                      src={it.dataUrl}
                      alt={t('capture.sectionOf', { n: i + 1, total: items.length })}
                      className="w-full"
                    />
                  ) : (
                    <div className="flex items-center gap-3 p-4">
                      <div className="text-3xl">📄</div>
                      <div className="min-w-0 flex-1">
                        <div className="line-clamp-2 break-all text-sm font-medium">{it.filename}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatBytes(it.size)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!uploading && canAddMore && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="rounded-md border-2 border-dashed border-muted-foreground/30 py-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  📷 {t('capture.addPhoto')}
                </button>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  className="rounded-md border-2 border-dashed border-muted-foreground/30 py-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  🖼️ {t('capture.fromGallery')}
                </button>
              </div>
            )}

            {uploading && (
              <div className="rounded-2xl border bg-card p-6 text-center">
                <div
                  className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary"
                  role="status"
                  aria-label={t('capture.processing')}
                />
                <div className="text-sm font-medium">{progress}</div>
                <div className="mt-2 text-xs tabular-nums text-muted-foreground">
                  {t('capture.workingElapsed', { seconds: elapsedSec })}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t('capture.workingHint')}
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            {!uploading && (
              <div className="flex gap-2">
                <button
                  onClick={handleReset}
                  className="flex-1 rounded-md border py-2 text-sm hover:bg-muted"
                >
                  {t('capture.clearAll')}
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  {hasPdf
                    ? t('capture.submitPdf')
                    : items.length > 1
                      ? t('capture.submitPhotos', { count: items.length })
                      : t('capture.submitReceipt')}
                </button>
              </div>
            )}
          </div>
        ))}

        {!listResult && mode === 'batch' && (batchItems.length === 0 ? (
          <div className="rounded-2xl border bg-card p-8 text-center">
            <p className="mb-4 text-sm text-muted-foreground">
              {t('capture.batchHint')}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                📷 {t('capture.takePhoto')}
              </button>
              <button
                onClick={() => galleryInputRef.current?.click()}
                className="rounded-md border px-6 py-3 text-sm font-medium hover:bg-muted"
              >
                🖼️ {t('capture.chooseImage')}
              </button>
            </div>
            {error && (
              <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <ul className="space-y-2">
              {batchItems.map((it, i) => {
                const elapsed =
                  it.startedAt && batchTick >= 0
                    ? Math.floor((Date.now() - it.startedAt) / 1000)
                    : 0;
                return (
                  <li
                    key={it.id}
                    className="rounded-2xl border bg-card p-3"
                  >
                    <div className="flex items-start gap-3">
                      <img
                        src={it.dataUrl}
                        alt={t('capture.batchSlot', {
                          n: i + 1,
                          total: batchItems.length,
                        })}
                        className="h-14 w-14 flex-shrink-0 rounded-md object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-muted-foreground">
                          {t('capture.batchSlot', {
                            n: i + 1,
                            total: batchItems.length,
                          })}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-sm">
                          {it.status === 'queued' && (
                            <span className="text-muted-foreground">
                              ⏸ {t('capture.batchStatusQueued')}
                            </span>
                          )}
                          {it.status === 'uploading' && (
                            <span className="flex items-center gap-1.5 text-primary">
                              <span
                                className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-muted border-t-primary"
                                aria-hidden
                              />
                              {t('capture.batchStatusUploading', {
                                seconds: elapsed,
                              })}
                            </span>
                          )}
                          {it.status === 'done' && (
                            <span className="font-medium text-emerald-600 dark:text-emerald-400">
                              ✓ {t('capture.batchStatusDone')}
                            </span>
                          )}
                          {it.status === 'failed' && (
                            <span className="font-medium text-destructive">
                              ✗ {t('capture.batchStatusFailed')}
                            </span>
                          )}
                        </div>
                        {it.status === 'failed' && it.error && (
                          <div className="mt-0.5 break-words text-xs text-destructive">
                            {it.error}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-1">
                        {it.status === 'done' && it.receiptId && (
                          <Link
                            to={`/receipts/${it.receiptId}`}
                            className="rounded-md border px-2 py-1 text-xs hover:bg-muted"
                          >
                            {t('capture.viewDetail')}
                          </Link>
                        )}
                        {!batchRunning &&
                          (it.status === 'queued' ||
                            it.status === 'failed') && (
                            <button
                              onClick={() => removeBatchItem(it.id)}
                              className="rounded px-1.5 py-0.5 text-destructive hover:bg-destructive/10"
                              aria-label={t('capture.remove')}
                            >
                              ✕
                            </button>
                          )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {batchCanAddMore && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="rounded-md border-2 border-dashed border-muted-foreground/30 py-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  📷 {t('capture.addPhoto')}
                </button>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  className="rounded-md border-2 border-dashed border-muted-foreground/30 py-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  🖼️ {t('capture.fromGallery')}
                </button>
              </div>
            )}

            <div className="rounded-2xl border bg-card px-4 py-3">
              <div className="text-xs tabular-nums text-muted-foreground">
                {batchRunning
                  ? t('capture.batchProcessing', {
                      done: batchDoneCount + batchFailedCount,
                      total: batchItems.length,
                      uploading: batchUploadingCount,
                    })
                  : batchAllSettled
                    ? batchFailedCount === 0
                      ? t('capture.batchAllDone', { count: batchDoneCount })
                      : t('capture.batchPartialDone', {
                          done: batchDoneCount,
                          failed: batchFailedCount,
                        })
                    : t('capture.batchSummary', {
                        done: batchDoneCount,
                        failed: batchFailedCount,
                        pending: batchQueuedCount,
                      })}
              </div>
              {batchRunning && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {t('capture.batchRunningHint')}
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            {batchRunning ? (
              <button
                onClick={handleBatchCancel}
                className="w-full rounded-md border py-2 text-sm hover:bg-muted"
              >
                {t('capture.batchCancel')}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleBatchReset}
                  className="flex-1 rounded-md border py-2 text-sm hover:bg-muted"
                >
                  {t('capture.clearAll')}
                </button>
                {batchHasFailed && (
                  <button
                    onClick={handleBatchRetryFailed}
                    className="flex-1 rounded-md bg-amber-600 py-2 text-sm font-medium text-white hover:opacity-90"
                  >
                    {t('capture.batchRetryFailed', { count: batchFailedCount })}
                  </button>
                )}
                {batchHasQueued && (
                  <button
                    onClick={handleBatchSubmit}
                    className="flex-1 rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                  >
                    {t('capture.batchSubmit', { count: batchQueuedCount })}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {!listResult && mode === 'cash' && (
          <div className="space-y-4">
            {!cashPreview && (
              <div className="rounded-2xl border bg-card p-4 space-y-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    {t('capture.cashEntry.textareaLabel')}
                  </label>
                  <textarea
                    value={cashText}
                    onChange={(e) => setCashText(e.target.value)}
                    placeholder={t('capture.cashEntry.textareaPlaceholder')}
                    rows={4}
                    maxLength={1000}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                  <div className="text-right text-xs text-muted-foreground mt-1">
                    {cashText.length}/1000
                  </div>
                </div>
                {parseTextMutation.isPending ? (
                  <div className="rounded-2xl border bg-card p-6 text-center">
                    <div
                      className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary"
                      role="status"
                      aria-label={t('capture.cashEntry.analyzing')}
                    />
                    <div className="text-sm font-medium">
                      {t('capture.cashEntry.analyzing')}
                    </div>
                    <div className="mt-2 text-xs tabular-nums text-muted-foreground">
                      {t('capture.workingElapsed', { seconds: elapsedSec })}
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => parseTextMutation.mutate(cashText.trim())}
                    disabled={!cashText.trim()}
                    className="w-full h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 active:opacity-80 disabled:opacity-50 transition"
                  >
                    {t('capture.cashEntry.analyze')}
                  </button>
                )}
                {cashError && !parseTextMutation.isPending && (
                  <p className="text-xs text-red-400">
                    {t('capture.cashEntry.parseFailed')}: {cashError}
                  </p>
                )}
              </div>
            )}

            {cashPreview && (
              <div className="rounded-2xl border bg-card p-4 space-y-3">
                {cashPreview.confidence < 0.3 && (
                  <div className="rounded-md border border-yellow-900/50 bg-yellow-900/20 p-2 text-xs">
                    {t('capture.cashEntry.lowConfidence')}
                  </div>
                )}

                <div className="text-xs text-muted-foreground">
                  {t('capture.cashEntry.originalText')}: <span className="italic">{cashPreview.raw_text}</span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs">
                    <span className="mb-1 block text-muted-foreground">{t('capture.cashEntry.merchant')}</span>
                    <input
                      type="text"
                      value={cashPreview.merchant ?? ''}
                      onChange={(e) =>
                        setCashPreview({ ...cashPreview, merchant: e.target.value || null })
                      }
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="mb-1 block text-muted-foreground">{t('capture.cashEntry.totalAmount')}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={cashPreview.total_amount ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCashPreview({
                          ...cashPreview,
                          total_amount: v === '' ? null : Number(v),
                        });
                      }}
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="mb-1 block text-muted-foreground">{t('receipt.dateFormat')}</span>
                    <input
                      type="date"
                      value={cashPreview.receipt_date}
                      onChange={(e) =>
                        setCashPreview({ ...cashPreview, receipt_date: e.target.value })
                      }
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block text-xs">
                    <span className="mb-1 block text-muted-foreground">{t('receipt.time')}</span>
                    <input
                      type="time"
                      value={cashPreview.receipt_time ?? ''}
                      onChange={(e) =>
                        setCashPreview({ ...cashPreview, receipt_time: e.target.value || null })
                      }
                      className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                    />
                  </label>
                </div>

                <label className="block text-xs">
                  <span className="mb-1 block text-muted-foreground">{t('receipt.category')}</span>
                  <select
                    value={cashPreview.category_id ?? ''}
                    aria-label={t('receipt.category')}
                    onChange={(e) =>
                      setCashPreview({ ...cashPreview, category_id: e.target.value || null })
                    }
                    className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                  >
                    <option value="">{t('receipt.categoryNone')}</option>
                    {cashCategoriesQuery.data?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <CategoryHint category={cashCategoriesQuery.data?.find(c=>c.id===cashPreview.category_id)} />
                </label>

                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">
                    {t('capture.cashEntry.itemsHeader')}
                  </div>
                  {cashPreview.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1 items-center">
                      <input
                        type="text"
                        value={item.name}
                        placeholder={t('capture.cashEntry.itemNamePlaceholder')}
                        onChange={(e) => {
                          const items = [...cashPreview.items];
                          items[idx] = { ...item, name: e.target.value };
                          setCashPreview({ ...cashPreview, items });
                        }}
                        className="col-span-6 rounded-md border bg-background px-2 py-1 text-xs"
                      />
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={item.quantity}
                        onChange={(e) => {
                          const items = [...cashPreview.items];
                          items[idx] = { ...item, quantity: Number(e.target.value) || 1 };
                          setCashPreview({ ...cashPreview, items });
                        }}
                        className="col-span-2 rounded-md border bg-background px-2 py-1 text-xs"
                      />
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={item.subtotal ?? ''}
                        placeholder={t('capture.cashEntry.itemSubtotalPlaceholder')}
                        onChange={(e) => {
                          const v = e.target.value;
                          const items = [...cashPreview.items];
                          items[idx] = { ...item, subtotal: v === '' ? null : Number(v) };
                          setCashPreview({ ...cashPreview, items });
                        }}
                        className="col-span-3 rounded-md border bg-background px-2 py-1 text-xs"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const items = cashPreview.items.filter((_, i) => i !== idx);
                          setCashPreview({ ...cashPreview, items });
                        }}
                        className="col-span-1 text-xs text-muted-foreground hover:text-red-400"
                        aria-label={t('common.remove')}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setCashPreview({
                        ...cashPreview,
                        items: [
                          ...cashPreview.items,
                          { name: '', quantity: 1, unit_price: null, subtotal: null },
                        ],
                      });
                    }}
                    className="w-full rounded-md border border-dashed bg-card/50 px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition"
                  >
                    + {t('capture.cashEntry.addItem')}
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setCashPreview(null);
                      setCashError(null);
                    }}
                    className="flex-1 h-11 rounded-lg border bg-card text-sm font-medium hover:bg-muted active:bg-muted/70 transition"
                  >
                    {t('capture.cashEntry.discard')}
                  </button>
                  <button
                    type="button"
                    disabled={
                      cashPreview.total_amount == null ||
                      cashPreview.total_amount <= 0 ||
                      saveManualMutation.isPending
                    }
                    onClick={() => {
                      if (cashPreview.total_amount == null) return;
                      const total = cashPreview.total_amount;
                      saveManualMutation.mutate({
                        merchant: cashPreview.merchant,
                        receipt_date: cashPreview.receipt_date,
                        receipt_time: cashPreview.receipt_time,
                        currency: cashPreview.currency,
                        total_amount: total,
                        category_id: cashPreview.category_id,
                        notes: cashPreview.raw_text,
                        ai_model: cashPreview.ai_model,
                        ai_confidence: cashPreview.confidence,
                        items: cashPreview.items
                          .filter((it) => it.subtotal != null && it.subtotal >= 0)
                          .map((it) => ({
                            name: it.name,
                            quantity: it.quantity,
                            unit_price: it.unit_price,
                            subtotal: it.subtotal as number,
                          })),
                      });
                    }}
                    className="flex-1 h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 active:opacity-80 disabled:opacity-50 transition"
                  >
                    {saveManualMutation.isPending
                      ? t('capture.cashEntry.saving')
                      : t('capture.cashEntry.save')}
                  </button>
                </div>
                {cashError && !saveManualMutation.isPending && (
                  <p className="text-xs text-red-400">
                    {t('capture.cashEntry.saveFailed')}: {cashError}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {!listResult && mode === 'savings' && (
          <SavingsInlineForm
            onDone={(id) => navigate(`/receipts/${id}`, { replace: true, state: { returnTo: '/ledger?category_id=sys_savings&date_scope=all' } })}
          />
        )}
      </div>
    </PageLayout>
  );
}

// Pay-yourself-first(0.2.50)— 在 Capture 页 chip 后渲染的轻量记一笔储蓄表单。
// 不走 OCR/Cash NL,直接调 POST /receipts/manual 写入 category_id='sys_savings'
// 的 receipt。receipt 自动 verified(verification_status 默认值)+ is_income=0,
// 但被 insights/financial-summary/alerts 里的 8+5 处 sys_savings exclude 排除,
// 不污染任何总支出/分类/商家/趋势聚合(参见 0.2.50 spec §4.2)。
function SavingsInlineForm({ onDone }: { onDone: (id: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [note, setNote] = useState('');

  const saveMutation = useMutation({
    mutationFn: (body: { amount: number; date: string; note: string }) =>
      api.post<{ id: string }>('/receipts/manual', {
        merchant: '储蓄转入',
        merchant_zh: '储蓄转入',
        receipt_date: body.date,
        currency: 'AUD',
        total_amount: body.amount,
        category_id: 'sys_savings',
        notes: body.note || null,
      }),
    onSuccess: async (saved) => {
      qc.invalidateQueries({ queryKey: ['insights'] });
      qc.invalidateQueries({ queryKey: ['alerts'] });
      // Home receipts list (key prefix ['receipts-v2', ...]) caches at
      // staleTime 30s — without this invalidation the just-saved 储蓄转入
      // row stays hidden until the user pulls to refresh or 30s elapses.
      // refetchType:'all' so the inactive Home-page observer refetches
      // immediately too, otherwise the next /home mount serves stale cache
      // for the few ms before the natural refetch kicks in (caught by
      // pay-yourself-first-walkthrough.spec line 192 in full-suite runs).
      await qc.invalidateQueries({
        queryKey: ['receipts-v2'],
        refetchType: 'all',
      });
      qc.invalidateQueries({ queryKey: ['receipts'] });
      toast.success(t('capture.savingsSuccess', { amount }));
      onDone(saved.id);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : t('capture.savingsError'));
    },
  });

  const numericAmount = Number(amount);
  const canSubmit =
    numericAmount > 0 && !Number.isNaN(numericAmount) && !saveMutation.isPending;

  return (
    <div className="space-y-4 rounded-2xl border bg-card p-5">
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            {t('capture.savingsFormAmount')}
          </label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full rounded-lg border bg-background px-3 py-2 text-lg tabular-nums"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            {t('capture.savingsFormDate')}
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-lg border bg-background px-3 py-2"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            {t('capture.savingsFormNote')}{' '}
            <span>({t('capture.savingsFormNoteHint')})</span>
          </label>
          <input
            type="text"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-lg border bg-background px-3 py-2"
          />
        </div>
      </div>

      <button
        type="button"
        disabled={!canSubmit}
        onClick={() =>
          saveMutation.mutate({ amount: numericAmount, date, note })
        }
        className="w-full rounded-xl bg-primary py-3 font-medium text-primary-foreground disabled:opacity-50"
      >
        {saveMutation.isPending
          ? t('capture.savingsFormSaving')
          : t('capture.savingsFormSave')}
      </button>
    </div>
  );
}
